<template>
  <div>
    <div v-if="!loading">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Job
          </h1>
        </template>
        <template #actions>
          <page-header-action
            tag="form-button"
            level="primary"
            @clicked="manuallyComplete()"
            :indicator="completing"
            v-if="!job?.completed_at && hasPermission('jobs.manuallyComplete')"
          >
            Manually Complete
          </page-header-action>

          <page-header-action
            tag="form-button"
            level="primary"
            @clicked="generateEnvelope()"
            v-if="!job.completed_at"
          >
            Generate Docusign
          </page-header-action>

          <page-header-action tag="a" :href="job?.url" v-if="canViewClaim">
            View Claim
          </page-header-action>

          <page-header-action
            tag="router-link"
            :to="`/jobs/${job.identifier}/edit`"
            v-if="!job?.completed_at && hasPermission('jobs.update')"
          >
            Edit Job
          </page-header-action>

          <page-header-action @click="toggleNotes">
            Notes
          </page-header-action>
        </template>
      </page-header>

      <div class="card mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <table class="table">
                <thead>
                  <tr>
                    <th colspan="2">
                      Job Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Job Type</td>
                    <td>
                      <job-label :job="job"></job-label>
                    </td>
                  </tr>
                  <tr>
                    <td>Date</td>
                    <td>
                      {{ job.date | date }}
                      <span v-if="job.start_time"
                        >{{ job.start_time | time
                        }}<span v-if="job.end_time">
                          - {{ job.end_time | time }}</span
                        ></span
                      >
                    </td>
                  </tr>
                  <tr>
                    <td>Claim Reference</td>
                    <td>
                      <jobable-claim-reference
                        :job="job"
                      ></jobable-claim-reference>
                    </td>
                  </tr>
                  <tr>
                    <td>Location</td>
                    <td>{{ job.location }}</td>
                  </tr>
                  <tr>
                    <td>Vehicle</td>
                    <td>{{ job.vehicle?.registration || 'Not Provided' }}</td>
                  </tr>
                  <tr
                    v-if="
                      job.type?.name === 'hire_delivery' ||
                        job.type?.name === 'hire_collection'
                    "
                  >
                    <td>Hire Period</td>
                    <td>
                      <job-hire-period :job="job"></job-hire-period>
                    </td>
                  </tr>
                  <tr v-if="job.description">
                    <td>Description</td>
                    <td>{{ job.description }}</td>
                  </tr>
                  <tr>
                    <td>Docusign Status</td>
                    <td>
                      <envelope-status-badge
                        :job="job"
                        :status="envelopes[0]?.status"
                      />
                    </td>
                  </tr>
                  <tr v-if="job.completed_at">
                    <td>Completed</td>
                    <td>
                      {{ job.completed_at | date }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-md-6">
              <div class="map" id="job-map"></div>
            </div>
          </div>
        </div>
      </div>

      <h3>Runs</h3>

      <div class="card mb-3">
        <table class="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Driver</th>
              <th>Reason</th>
              <th>Completed At</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="job.items.length">
            <tr :key="key" v-for="(item, key) in job.items">
              <td>{{ item.run?.date | date }}</td>
              <td>{{ item.run?.driver?.full_name }}</td>
              <td>{{ item.reason?.label }}</td>
              <td>{{ item.completed_at | date(dateTimeFormat) }}</td>
              <td><job-status :item="item"></job-status></td>
              <td>
                <form-button
                  level="primary"
                  :indicator="downloading === item.id"
                  @clicked="download(item)"
                >
                  Download PDF
                </form-button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="5">
                This job has not been assigned to any runs
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <envelopes
        :job="job"
        :envelopes="envelopes"
        @envelope-updated="updateGenerateEnvelope"
        v-if="envelopes.length"
      ></envelopes>

      <div v-if="uploads.length">
        <h3 class="mt-3">Uploads</h3>

        <div class="row">
          <div
            class="col-md-4 pb-3"
            :key="index"
            v-for="(upload, index) in uploads"
          >
            <div class="card h-100">
              <img :src="upload.web_url" class="card-img-top" />
              <div class="card-body text-center">
                <a :href="upload.url" target="_blank" class="btn btn-white">
                  View Original
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <job-notes
        :job="job"
        :show="showNotes"
        @saved="addNote"
        @closed="closeNotes"
      ></job-notes>
    </div>

    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import JobLabel from '@/components/Modules/Shared/JobLabel';
import JobHirePeriod from '@/components/Modules/Shared/JobHirePeriod';
import JobNotes from '@/components/Modules/Job/Children/JobNotes';
import JobStatus from '@/components/Modules/Job/Children/JobStatus';
import JobableClaimReference from '@/components/Modules/Job/Children/JobableClaimReference';
import Envelopes from '@/components/Modules/Job/Children/Envelopes';
import EnvelopeStatusBadge from '@/components/Modules/Shared/EnvelopeStatusBadge.vue';
import FormButton from '@/components/Generic/Form/FormButton.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    id: {
      type: String,
      required: true
    }
  },

  components: {
    FormButton,
    PageHeader,
    PageHeaderAction,
    EnvelopeStatusBadge,
    JobLabel,
    JobHirePeriod,
    JobNotes,
    JobStatus,
    JobableClaimReference,
    Envelopes
  },

  data() {
    return {
      loading: true,
      completing: false,
      job: null,
      map: undefined,
      dateTimeFormat: 'DD/MM/YYYY HH:mm',
      downloading: undefined,
      showNotes: false,
      envelopes: []
    };
  },

  computed: {
    canViewClaim() {
      return !!this.job?.jobable?.source || !!this.job?.jobable?.incident;
    },

    uploads() {
      return this.job?.items.map(item => item.uploads).flat() ?? [];
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      window.Ajax.get(`/jobs/${this.id}?parts=items,notes,type,jobable`)
        .then(({ data }) => {
          this.job = data;
          this.envelopes = this.job?.envelopes;
          this.loading = false;

          setTimeout(() => {
            this.setMap();
          }, 100);
        })
        .catch(err => {
          window.toastify.error(
            'Oops',
            'An error occurred while loading the job'
          );
        });
    },
    setMap() {
      window.GoogleMapsLoader.load(google => {
        let options = {
          center: {
            lat: parseFloat(this.job.latitude),
            lng: parseFloat(this.job.longitude)
          },
          zoom: 14
        };

        this.map = new google.maps.Map(
          document.getElementById('job-map'),
          options
        );

        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(
            this.job.latitude,
            this.job.longitude
          )
        });

        marker.setMap(this.map);
      });
    },

    download(item) {
      this.downloading = item.id;

      window.Ajax.download(
        `/runs/${item.run.identifier}/items/${item.identifier}/download`
      )
        .then(() => (this.downloading = undefined))
        .catch(err => {
          this.downloading = undefined;

          window.toastify.error(
            'Oops',
            'An error occurred while downloading the PDF'
          );
        });
    },

    generateEnvelope() {
      this.route(`/jobs/${this.job.identifier}/generate-docusign`);
    },

    toggleNotes() {
      this.showNotes = !this.showNotes;
    },

    addNote(note) {
      this.job.notes.push(note);
    },

    closeNotes() {
      this.showNotes = false;
    },

    updateGenerateEnvelope() {
      this.mount();
    },

    manuallyComplete() {
      window
        .swal({
          title: 'Are you sure?',
          text: 'Are you sure you want to mark that job as complete?',
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          this.completing = true;

          window.Ajax.put(
            `/jobs/${this.job.identifier}/manually-complete?parts=items,notes,type,jobable`,
            []
          )
            .then(({ data }) => {
              this.job = data;
              this.completing = false;

              window.toastify.success(
                'Success!',
                'The job was completed successfully!'
              );
            })
            .catch(error => {
              this.completing = false;
              window.toastify.error(
                'Error',
                'Sorry, there was an error completing the job.'
              );
            });
        });
    }
  }
};
</script>

<style lang="scss">
.map {
  width: 100%;
  height: 300px;
}
</style>
